import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Heart, MapPin, MessageSquare, Trash2 } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { CUSTOMER_NAV } from "@/lib/nav";
import { listFavourites, toggleFavourite } from "@/lib/favourites";
import { fetchPropertyDetail, type PropertyDetail } from "@/lib/property-details";
import { listCustomerEnquiries, ENQUIRY_STATUS_CLASS, ENQUIRY_STATUS_LABEL } from "@/lib/enquiries";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/_authenticated/customer/favourites")({
  head: () => ({
    meta: [
      { title: "Favourites · Landoo" },
      {
        name: "description",
        content: "Your saved Landoo listings and the enquiries you sent on them.",
      },
    ],
  }),
  component: FavouritesPage,
});

function FavouritesPage() {
  const { profile } = useAuth();
  const [ids, setIds] = useState<string[]>([]);

  useEffect(() => setIds(listFavourites()), []);

  const saved = useQuery({
    queryKey: ["favourite-listings", ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const rows = await Promise.all(ids.map((id) => fetchPropertyDetail(id).catch(() => null)));
      return rows.filter((r): r is PropertyDetail => Boolean(r));
    },
  });

  const enquiries = useQuery({
    queryKey: ["customer-enquiries", profile?.id],
    enabled: Boolean(profile?.id),
    queryFn: () => listCustomerEnquiries(profile!.id),
  });

  const remove = (id: string) => {
    toggleFavourite(id);
    setIds(listFavourites());
  };

  const savedList = saved.data ?? [];
  const savedIdSet = new Set(savedList.map((l) => l.id));
  const relatedEnquiries = (enquiries.data ?? []).filter(
    (e) => e.listing_id && savedIdSet.has(e.listing_id),
  );

  return (
    <DashboardShell title="Favourites" role="Customer" items={CUSTOMER_NAV}>
      <div className="space-y-6">
        <section>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-brown-dark">
            <Heart className="h-4 w-4 text-coral" /> Saved listings
            <span className="text-xs font-normal text-muted-foreground">({ids.length})</span>
          </h2>

          {ids.length === 0 ? (
            <EmptyCard
              title="No saved listings yet"
              body="Tap the heart on any property to save it here for quick access — even offline."
              to="/"
              cta="Explore the map"
            />
          ) : saved.isLoading ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {ids.slice(0, 3).map((id) => (
                <div key={id} className="h-56 animate-pulse rounded-2xl bg-secondary/60" />
              ))}
            </div>
          ) : savedList.length === 0 ? (
            <EmptyCard
              title="Saved listings unavailable"
              body="These listings are no longer published. Save new ones from the map."
              to="/"
              cta="Explore the map"
            />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {savedList.map((listing) => (
                <div
                  key={listing.id}
                  className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-soft"
                >
                  <Link
                    to="/property/$id"
                    params={{ id: listing.id }}
                    className="group block min-w-0"
                  >
                    <img
                      loading="lazy"
                      decoding="async"
                      src={listing.images[0]}
                      alt={listing.title}
                      className="h-40 w-full object-cover"
                    />
                    <div className="space-y-1.5 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="line-clamp-1 min-w-0 text-sm font-semibold text-brown-dark">
                          {listing.title}
                        </h3>
                        <span className="shrink-0 text-sm font-bold text-coral">
                          {listing.priceLabel}
                        </span>
                      </div>
                      <div className="flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0" />
                        <span className="truncate">{listing.address}</span>
                      </div>
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        {listing.kind} · {listing.propertyType}
                      </div>
                    </div>
                  </Link>
                  <button
                    type="button"
                    onClick={() => remove(listing.id)}
                    className="flex items-center justify-center gap-1.5 border-t border-border px-3 py-2 text-[11px] font-semibold text-muted-foreground hover:text-coral"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove from favourites
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-brown-dark">
            <MessageSquare className="h-4 w-4" /> Enquiries on saved listings
          </h2>
          {relatedEnquiries.length === 0 ? (
            <EmptyCard
              title="No enquiries on your saved listings"
              body="Send an enquiry from any saved property and its status appears here."
              to="/customer/enquiries"
              cta="View all enquiries"
            />
          ) : (
            <ul className="space-y-2">
              {relatedEnquiries.map((e) => (
                <li
                  key={e.id}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-soft"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-brown-dark">
                      {e.listing_title ?? "Listing"}
                    </div>
                    <p className="line-clamp-1 text-[11px] text-muted-foreground">
                      {e.message ?? "—"}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold ${ENQUIRY_STATUS_CLASS[e.enquiry_status]}`}
                  >
                    {ENQUIRY_STATUS_LABEL[e.enquiry_status]}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </DashboardShell>
  );
}

function EmptyCard({
  title,
  body,
  to,
  cta,
}: {
  title: string;
  body: string;
  to: string;
  cta: string;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/60 p-6 text-center">
      <div className="text-sm font-semibold text-brown-dark">{title}</div>
      <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">{body}</p>
      <Link
        to={to}
        className="mt-3 inline-flex items-center rounded-full bg-brown px-4 py-2 text-xs font-semibold text-primary-foreground"
      >
        {cta}
      </Link>
    </div>
  );
}
